/**
 * pages/procurement/BudgetReportPage.jsx — printable budget report.
 *
 * Standalone (no MainLayout chrome) so it prints / saves-to-PDF cleanly.
 * Toolbar: Print, Download Excel, Generate AI executive summary.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { ArrowLeft, Printer, Download, Sparkles, Loader2 } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  getReport, generateReportSummary, downloadReportXlsx,
} from '../../api/budgets';
import { getCurrentAccount } from '../../lib/auth';
import { HORIZONS } from '../../api/budgets';

const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const horizonLabel = (m) => HORIZONS.find((h) => h.value === m)?.label || `${m} mo`;
const COLORS = { supply: '#0ea5e9', delivery: '#6366f1', tax: '#f59e0b', added: '#94a3b8' };

const Kpi = ({ label, value, sub }) => (
  <div className="border rounded-md p-4">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-2xl font-bold mt-1 tracking-tight">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export default function BudgetReportPage() {
  const { budgetId } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const account = getCurrentAccount();
  const accountId = account?.id;

  const filters = {
    property: sp.get('property') || undefined,
    state: sp.get('state') || undefined,
    commodity: sp.get('commodity') || undefined,
    meter: sp.get('meter') || undefined,
  };
  const scenarioId = sp.get('scenario') || undefined;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [genning, setGenning] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const r = await getReport({ accountId, id: budgetId, scenarioId, filters });
      setReport(r);
    } catch (e) {
      toast.error('Failed to load report', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, budgetId, scenarioId]);

  useEffect(() => { load(); }, [load]);

  const genSummary = async () => {
    setGenning(true);
    try {
      const res = await generateReportSummary({ accountId, id: budgetId, scenarioId, filters });
      setAiSummary(res.summary);
      toast.success('Executive summary generated', { description: `${res.ai.balance} AI credits left` });
    } catch (e) {
      toast.error('Summary failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setGenning(false);
    }
  };

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      await downloadReportXlsx({ accountId, id: budgetId, scenarioId, filters, budgetName: report?.budget?.name });
    } catch (e) {
      toast.error('Excel export failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Building report…</div>;
  }
  if (!report) return <div className="p-8">Report not available.</div>;

  const b = report.budget;
  const t = report.totals || {};
  const chartData = (report.by_month || []).map((m) => ({
    month: m.month.slice(2), supply: m.supply, delivery: m.delivery, tax: m.tax, added: m.added,
  }));
  const bva = report.budget_vs_actual;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>

      {/* Toolbar */}
      <div className="no-print sticky top-0 z-10 bg-white/90 backdrop-blur border-b px-6 py-3 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/procurement/budgets/${budgetId}`)} data-testid="report-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={genSummary} disabled={genning} data-testid="report-ai-summary-btn">
            {genning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5 text-violet-600" />} AI summary
          </Button>
          <Button variant="outline" size="sm" onClick={downloadXlsx} disabled={downloading} data-testid="report-excel-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Excel
          </Button>
          <Button size="sm" onClick={() => window.print()} data-testid="report-print-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Report body */}
      <div className="max-w-[1000px] mx-auto px-8 py-8 space-y-8" data-testid="budget-report">
        <div className="flex items-start justify-between border-b pb-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Budget Report</div>
            <h1 className="text-3xl font-bold tracking-tight mt-1">{b.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline">{report.scenario?.name || 'Base'}</Badge>
              <Badge variant="outline">{horizonLabel(b.horizon_months)}</Badge>
              <Badge variant="outline">Start {b.start_month}</Badge>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div>{account?.organisation_name || account?.name}</div>
            <div>{new Date(report.generated_at).toLocaleDateString()}</div>
          </div>
        </div>

        {/* AI exec summary */}
        {aiSummary && (
          <div className="border-l-4 border-violet-400 bg-violet-50/50 p-4 rounded-r" data-testid="report-exec-summary">
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Executive Summary</div>
            <p className="text-sm leading-relaxed whitespace-pre-line">{aiSummary}</p>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Projected Total" value={money(t.total)} sub={`over ${b.horizon_months} months`} />
          <Kpi label="Supply" value={money(t.supply)} sub={t.total ? `${Math.round((t.supply / t.total) * 100)}%` : ''} />
          <Kpi label="Delivery" value={money(t.delivery)} sub={t.total ? `${Math.round((t.delivery / t.total) * 100)}%` : ''} />
          <Kpi label="Tax + Added" value={money((t.tax || 0) + (t.added || 0))} />
        </div>

        {/* Monthly chart */}
        <div>
          <h2 className="text-sm font-semibold mb-3">Monthly projection — supply vs delivery</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="supply" stackId="a" fill={COLORS.supply} name="Supply" />
              <Bar dataKey="delivery" stackId="a" fill={COLORS.delivery} name="Delivery" />
              <Bar dataKey="tax" stackId="a" fill={COLORS.tax} name="Tax" />
              <Bar dataKey="added" stackId="a" fill={COLORS.added} name="Added" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* By commodity */}
        {(report.by_commodity || []).length > 0 && (
          <div>
            <h2 className="text-sm font-semibold mb-3">By commodity</h2>
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
                <th className="text-left py-2">Commodity</th><th className="text-right">Supply</th>
                <th className="text-right">Delivery</th><th className="text-right">Tax</th><th className="text-right">Total</th>
              </tr></thead>
              <tbody>
                {report.by_commodity.map((c) => (
                  <tr key={c.commodity} className="border-b last:border-0">
                    <td className="py-1.5 capitalize">{c.commodity}</td>
                    <td className="text-right">{money(c.supply)}</td>
                    <td className="text-right">{money(c.delivery)}</td>
                    <td className="text-right">{money(c.tax)}</td>
                    <td className="text-right font-medium">{money(c.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Budget vs Actual */}
        {bva?.rows?.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold mb-3">Budget vs Actual (elapsed)</h2>
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
                <th className="text-left py-2">Month</th><th className="text-right">Budget</th>
                <th className="text-right">Actual</th><th className="text-right">Variance</th>
              </tr></thead>
              <tbody>
                {bva.rows.map((r) => (
                  <tr key={r.month} className="border-b last:border-0">
                    <td className="py-1.5">{r.month}</td>
                    <td className="text-right">{money(r.budget)}</td>
                    <td className="text-right">{money(r.actual)}</td>
                    <td className={`text-right ${r.variance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{money(r.variance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Top properties */}
        <div>
          <h2 className="text-sm font-semibold mb-3">Top properties</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
              <th className="text-left py-2">Property</th>
              {b.granularity === 'meter' && <th className="text-left">Meter</th>}
              <th className="text-left">Commodity</th><th className="text-right">Supply</th>
              <th className="text-right">Delivery</th><th className="text-right">Total</th>
            </tr></thead>
            <tbody>
              {(report.by_scope || []).slice(0, 25).map((s) => (
                <tr key={s.scope_key} className="border-b last:border-0">
                  <td className="py-1.5 max-w-[280px] truncate" title={s.property_name}>{s.property_name}</td>
                  {b.granularity === 'meter' && <td className="text-xs">{s.meter_number || '—'}</td>}
                  <td className="capitalize">{s.commodity}</td>
                  <td className="text-right">{money(s.supply)}</td>
                  <td className="text-right">{money(s.delivery)}</td>
                  <td className="text-right font-medium">{money(s.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-xs text-slate-400 border-t pt-4">
          Generated {new Date(report.generated_at).toLocaleString()} · {report.scope_count} scope(s) ·
          assumptions per scenario "{report.scenario?.name}".
        </div>
      </div>
    </div>
  );
}
